import { useMemo } from 'react'

import { useQuery } from '@connectrpc/connect-query'

import { listContracts } from '@enechain/eposition-proto/bffcommon/v1/eex_service-EEXService_connectquery'
import { AreaCode } from '@enechain/eposition-proto/bfftypes/v1/area_pb'
import {
  DeliveryHourType,
  DeliveryPeriodType,
} from '@enechain/eposition-proto/types/v1/deal_pb'
import {
  EEXContractModel,
  convertAreaLabelFromAreaCode,
  convertDeliveryHourTypeLabel,
  convertDeliveryPeriodTypeLabel,
  convertEEXContractModelList,
  filterEEXContractByAreaCode,
  filterEEXContractByDeliveryPeriodType,
} from '@enechain/eposition-ui'
import { filterEEXContractByDeliveryHourType } from '@enechain/eposition-ui/src/models/eexContract'

type Option<T = string> = {
  label: string
  value: T
}

type Props = {
  isSummary: boolean
  selectedAreaCode: AreaCode
  selectedDeliveryPeriodType: DeliveryPeriodType
  selectedDeliveryHourType: DeliveryHourType
  isPhysicalFuture: boolean
}

// memo: 幾らか enum を radio UI のために string に mapping している
type Result = {
  areas: Array<Option<AreaCode>>
  deliveryHourTypes: Array<Option<DeliveryHourType>>
  deliveryPeriodTypes: Option[]
  eexContracts: EEXContractModel[]
}

export function useTransactionFormOption({
  isSummary,
  selectedAreaCode,
  selectedDeliveryPeriodType,
  selectedDeliveryHourType,
  isPhysicalFuture,
}: Props): Result {
  const { data: eexContractData } = useQuery(
    listContracts,
    {},
    { throwOnError: false },
  )
  const areas = useMemo(() => {
    const areaValues = Object.values(AreaCode)
      .filter((value) => typeof value !== 'string')
      .filter((value): value is AreaCode => {
        if (!isPhysicalFuture) {
          return value !== AreaCode.UNSPECIFIED
        }
        return (
          value === AreaCode.KANSAI ||
          value === AreaCode.TOKYO ||
          value === AreaCode.CHUBU
        )
      })

    return areaValues.map((value) => ({
      label: convertAreaLabelFromAreaCode(value),
      value,
    }))
  }, [isPhysicalFuture])

  const deliveryHourTypes = useMemo(() => {
    const deliveryHours = Object.values(DeliveryHourType).filter(
      (value) =>
        typeof value !== 'string' && value !== DeliveryHourType.UNSPECIFIED,
    )

    return deliveryHours.map((value) => ({
      label: convertDeliveryHourTypeLabel(value),
      value,
    }))
  }, [])

  const deliveryPeriodTypes = useMemo(() => {
    return Object.values(DeliveryPeriodType)
      .filter(
        (value) =>
          typeof value === 'number' &&
          (value === DeliveryPeriodType.MONTH ||
            (isSummary && value === DeliveryPeriodType.MULTI_MONTH) ||
            value === DeliveryPeriodType.QUARTER ||
            value === DeliveryPeriodType.SEASON ||
            (isSummary && value === DeliveryPeriodType.FISCAL_YEAR)),
      )
      .map((value) => {
        return {
          label: convertDeliveryPeriodTypeLabel(value as DeliveryPeriodType),
          value: value.toString(),
        }
      })
  }, [isSummary])

  const eexContracts = useMemo(() => {
    if (
      eexContractData?.contracts === undefined ||
      eexContractData.contracts.length === 0
    ) {
      return []
    }

    const filteredByAreaCode = filterEEXContractByAreaCode({
      eexContracts: eexContractData.contracts,
      areaCode: selectedAreaCode,
    })

    const filteredByDeliveryPeriodType = filterEEXContractByDeliveryPeriodType({
      eexContracts: filteredByAreaCode,
      deliveryPeriodType: selectedDeliveryPeriodType,
    })

    const filteredByDeliveryHourType = filterEEXContractByDeliveryHourType({
      eexContracts: filteredByDeliveryPeriodType,
      deliveryHourType: selectedDeliveryHourType,
    })

    return convertEEXContractModelList({
      value: filteredByDeliveryHourType,
      useEslLabel: true,
    })
  }, [
    eexContractData,
    selectedAreaCode,
    selectedDeliveryPeriodType,
    selectedDeliveryHourType,
  ])

  return {
    areas,
    deliveryHourTypes,
    deliveryPeriodTypes,
    eexContracts,
  }
}
